import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { useLang } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarLight from "@/components/NavbarLight";
import FooterLight from "@/components/FooterLight";
import RequestAccessModal from "@/components/RequestAccessModal";
import WinnersSection from "@/components/WinnersSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-page Google Analytics 4 tracking IDs.
// Add more entries here to enable GA on additional partner campaign pages.
const PAGE_GA_IDS = {
  fotbollsmorgon: "G-CZQQBN9RE5",
  scandsports: "G-PCQ6YTDZM0",
};

function loadGtag(gaId) {
  if (!gaId) return;
  // Ensure the base gtag.js loader is present (only once per gaId).
  const loaderId = `ga-loader-${gaId}`;
  if (!document.getElementById(loaderId)) {
    const s1 = document.createElement("script");
    s1.id = loaderId;
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s1);
  }
  // Initialize dataLayer / gtag shim exactly once.
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
  }
  // Configure this measurement ID (idempotent — gtag handles duplicate config).
  window.gtag("config", gaId, {
    page_path: window.location.pathname + window.location.search,
  });
}

export default function PageView() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const r = await axios.get(`${API}/pages/${slug}`);
        setPage(r.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Per-page Google Analytics injection. Fires when the campaign page loads
  // and again if the slug changes (SPA navigation).
  useEffect(() => {
    const gaId = PAGE_GA_IDS[slug];
    if (gaId) loadGtag(gaId);
  }, [slug]);

  // Delegate clicks on anchors with data-open-modal="request-access" (used by
  // partner-page VIP CTA buttons rendered from server-side HTML) to open the
  // "Skräddarsydd upplevelse" modal in-app instead of navigating.
  useEffect(() => {
    const handleClick = (e) => {
      const a = e.target.closest('a[data-open-modal="request-access"], a[href="#skraddarsydd-upplevelse"]');
      if (a) { e.preventDefault(); setShowRequestModal(true); }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>
      </>
    );
  }

  if (notFound || !page) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-center px-6">
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-white mb-4">404</h2>
            <p className="text-white/50 font-['Outfit'] font-light mb-6">{lang === "sv" ? "Sidan hittades inte" : "Page not found"}</p>
            <Link to="/" className="text-[#C5A059] font-['Outfit'] text-sm hover:underline">{t("return_homepage")}</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Pick localized fields with fallback to the other language
  const pick = (base) => page[`${base}_${lang}`] || page[`${base}_${lang === "sv" ? "en" : "sv"}`] || "";
  const title = pick("title");
  const metaTitle = pick("meta_title") || title;
  const metaDescription = pick("meta_description");
  const heroTitle = pick("hero_title");
  const heroSubtitle = pick("hero_subtitle");
  const heroCtaLabel = pick("hero_cta_label");
  const contentHtml = pick("content_html");
  // Split content on <!-- WINNERS_SECTION_MARKER --> so we can render the dynamic
  // <WinnersSection> component in the middle of the CMS HTML for eligible pages.
  const winnersMarker = "<!-- WINNERS_SECTION_MARKER -->";
  const winnersContestSlug = ["fotbollsmorgon", "scandsports"].includes(page.slug) ? page.slug : null;
  const [contentBeforeWinners, contentAfterWinners] = (winnersContestSlug && contentHtml.includes(winnersMarker))
    ? contentHtml.split(winnersMarker)
    : [contentHtml, ""];
  const safeContentBefore = contentBeforeWinners ? DOMPurify.sanitize(contentBeforeWinners, { USE_PROFILES: { html: true } }) : "";
  const safeContentAfter = contentAfterWinners ? DOMPurify.sanitize(contentAfterWinners, { USE_PROFILES: { html: true } }) : "";
  const ogImage = page.og_image || page.hero_image_url;
  const isLight = page.theme === "light";
  const accent = page.accent_color || "#C5A059"; // Serava gold by default; partners can override (e.g. ICA red)

  // ── Theme tokens (light vs dark) ──
  const tokens = isLight
    ? {
        pageBg: "bg-white",
        sectionFallbackBg: "bg-white",
        backLink: "text-black/50 hover:text-[#C5A059]",
        heroTitle: "text-[#1A1A1A]",
        heroSubtitle: "text-black/70",
        heroOverlay: "bg-gradient-to-t from-white via-white/70 to-white/30",
        plainTitle: "text-[#1A1A1A]",
        prose: "prose max-w-none font-['Outfit'] text-[#1A1A1A] prose-headings:font-['Cormorant_Garamond'] prose-headings:font-light prose-headings:text-[#1A1A1A] prose-a:text-[#C5A059] prose-strong:text-[#1A1A1A] prose-blockquote:border-l-[#C5A059] prose-img:rounded",
      }
    : {
        pageBg: "bg-[#0A0A0A]",
        sectionFallbackBg: "bg-[#0A0A0A]",
        backLink: "text-white/50 hover:text-[#C5A059]",
        heroTitle: "text-white",
        heroSubtitle: "text-white/70",
        heroOverlay: "bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/30",
        plainTitle: "text-white",
        prose: "prose prose-invert max-w-none font-['Outfit'] text-white/80 prose-headings:font-['Cormorant_Garamond'] prose-headings:font-light prose-headings:text-white prose-a:text-[#C5A059] prose-strong:text-white prose-blockquote:border-l-[#C5A059] prose-img:rounded",
      };

  const HeaderComponent = isLight ? NavbarLight : Navbar;
  const FooterComponent = isLight ? FooterLight : Footer;

  return (
    <div className={`min-h-screen ${tokens.pageBg}`} data-page-theme={isLight ? "light" : "dark"} data-page-accent={accent} data-testid={`page-view-${isLight ? "light" : "dark"}`}>
      {/* Per-page accent override — replaces the global gold .btn-primary so partner-branded CTAs match the page (e.g. ICA red) */}
      {page.accent_color && (
        <style>{`
          [data-page-accent="${accent}"] .btn-primary { background-color: ${accent} !important; color: #ffffff !important; }
          [data-page-accent="${accent}"] .btn-primary:hover { background-color: ${accent} !important; filter: brightness(0.92); }
          [data-page-accent="${accent}"] a[data-testid="page-hero-cta"] { background-color: ${accent}; color: #ffffff; }
        `}</style>
      )}
      <HeaderComponent />
      <main className={`min-h-screen ${tokens.pageBg}`}>
        <Helmet>
          <title>{metaTitle} — Serava Sports</title>
          {metaDescription && <meta name="description" content={metaDescription} />}
          <meta property="og:title" content={metaTitle} />
          {metaDescription && <meta property="og:description" content={metaDescription} />}
          {ogImage && <meta property="og:image" content={ogImage} />}
          <meta property="og:type" content="website" />
          <link rel="canonical" href={`https://seravasports.com/p/${page.slug}`} />
        </Helmet>

        {/* Hero */}
        {(page.hero_image_url || heroTitle) && (
          <section className="relative">
            {page.hero_image_url && (
              <>
                <div className="absolute inset-0 z-0">
                  <img src={page.hero_image_url} alt="" className="w-full h-full object-cover" />
                  <div className={`absolute inset-0 ${tokens.heroOverlay}`} />
                </div>
              </>
            )}
            <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-24 md:pt-40 md:pb-32 min-h-[60vh] flex flex-col justify-end">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Link to="/" className={`inline-flex items-center gap-2 text-sm font-['Outfit'] mb-6 transition-colors ${tokens.backLink}`}>
                  <ArrowLeft size={14} /> {t("back_to_events")}
                </Link>
                <h1 className={`font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05] tracking-tight mb-4 ${tokens.heroTitle}`}>{heroTitle || title}</h1>
                {heroSubtitle && <p className={`font-['Outfit'] font-light text-lg max-w-2xl leading-relaxed mb-8 ${tokens.heroSubtitle}`}>{heroSubtitle}</p>}
                {heroCtaLabel && page.hero_cta_url && (
                  <a href={page.hero_cta_url} data-testid="page-hero-cta" className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.15em] uppercase font-['Outfit']">
                    {heroCtaLabel} <ArrowRight size={14} />
                  </a>
                )}
              </motion.div>
            </div>
          </section>
        )}

        {/* If no hero image — show plain page title */}
        {!page.hero_image_url && !heroTitle && (
          <section className="max-w-5xl mx-auto px-6 pt-28 pb-12">
            <Link to="/" className={`inline-flex items-center gap-2 text-sm font-['Outfit'] mb-6 transition-colors ${tokens.backLink}`}>
              <ArrowLeft size={14} /> {t("back_to_events")}
            </Link>
            <h1 className={`font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05] tracking-tight ${tokens.plainTitle}`}>{title}</h1>
          </section>
        )}

        {/* Content */}
        {(safeContentBefore || safeContentAfter) && (
          <section className="max-w-3xl mx-auto px-6 py-12 md:py-20">
            {safeContentBefore && (
              <div
                className={tokens.prose}
                dangerouslySetInnerHTML={{ __html: safeContentBefore }}
              />
            )}
            {winnersContestSlug && contentHtml.includes(winnersMarker) && (
              <WinnersSection contestSlug={winnersContestSlug} lang={lang} />
            )}
            {safeContentAfter && (
              <div
                className={tokens.prose}
                dangerouslySetInnerHTML={{ __html: safeContentAfter }}
              />
            )}
          </section>
        )}

        {/* Linked event */}
        {page.linked_event_slug && (
          <section className="max-w-3xl mx-auto px-6 pb-16 md:pb-24">
            <Link to={`/event/${page.linked_event_slug}`} className="inline-flex items-center gap-2 text-[#C5A059] font-['Outfit'] text-sm hover:opacity-80 transition-opacity border-b border-[#C5A059]/40 pb-1">
              {lang === "sv" ? "Se eventet" : "View event"} <ArrowRight size={14} />
            </Link>
          </section>
        )}
      </main>
      <FooterComponent />
      {showRequestModal && <RequestAccessModal onClose={() => setShowRequestModal(false)} />}
    </div>
  );
}
